import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Loader2, Building2, CalendarPlus, BarChart3, Banknote, Wallet, LifeBuoy, UserRound, CheckCircle2, Clock } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('eventism_token')}` } });

function PartnerLogin() {
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await login(email, password); }
    catch (err) { toast.error(err.response?.data?.detail || 'Login failed — check your email and password'); }
    finally { setBusy(false); }
  };
  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-md border-indigo-500/30" data-testid="partner-login-card">
        <CardContent className="py-8 px-6 space-y-4">
          <div className="text-center">
            <Building2 className="h-10 w-10 text-indigo-400 mx-auto mb-2" />
            <h1 className="text-2xl font-extrabold">Eventism Partners</h1>
            <p className="text-sm text-muted-foreground">Venue & organizer portal — publish events, track sales, get paid.</p>
          </div>
          <form onSubmit={submit} className="space-y-3">
            <Input type="email" placeholder="Business email" value={email} onChange={e => setEmail(e.target.value)} required data-testid="partner-login-email" />
            <Input type="password" placeholder="Password" value={password} onChange={e => setPassword(e.target.value)} required data-testid="partner-login-password" />
            <Button type="submit" className="w-full bg-indigo-600 hover:bg-indigo-700 text-white" disabled={busy} data-testid="partner-login-submit">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Sign in to Partner Hub'}
            </Button>
          </form>
          <div className="text-center text-xs text-muted-foreground space-y-1">
            <p><a href="/forgot-password" className="text-indigo-400 hover:underline">Forgot password?</a></p>
            <p>New venue or organizer? <a href="/register" className="text-indigo-400 hover:underline">Create an account</a>, then apply here.</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function ApplicationForm({ onDone }) {
  const [form, setForm] = useState({ type: 'organizer', org_name: '', phone: '', city: '', website: '', about: '' });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async () => {
    if (!form.org_name || !form.about) return toast.error('Business name and a short description are required');
    setBusy(true);
    try {
      await axios.post(`${API}/partners/apply`, form, auth());
      toast.success('Application submitted — we review within 1 business day');
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not submit application'); }
    finally { setBusy(false); }
  };
  return (
    <Card className="border-indigo-500/30"><CardContent className="py-4 space-y-3" data-testid="partner-apply-form">
      <p className="text-sm font-semibold">Apply to become an Eventism partner</p>
      <p className="text-xs text-muted-foreground">A short application keeps the platform quality high — most are approved within 1 business day.</p>
      <div className="flex gap-2">
        {['organizer', 'venue'].map(t => (
          <button key={t} onClick={() => setForm({ ...form, type: t })}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border ${form.type === t ? 'bg-indigo-600 text-white border-indigo-600' : 'border-border text-muted-foreground'}`}
            data-testid={`apply-type-${t}`}>{t === 'organizer' ? 'Event organizer' : 'Venue'}</button>
        ))}
      </div>
      <div className="grid sm:grid-cols-2 gap-2">
        <Input placeholder="Business / venue name *" value={form.org_name} onChange={set('org_name')} data-testid="apply-org-name" />
        <Input placeholder="Phone" value={form.phone} onChange={set('phone')} data-testid="apply-phone" />
        <Input placeholder="City" value={form.city} onChange={set('city')} data-testid="apply-city" />
        <Input placeholder="Website or Instagram" value={form.website} onChange={set('website')} data-testid="apply-website" />
      </div>
      <Textarea placeholder="Tell us about your events or venue — what do you run, how often, typical crowd size? *" rows={3} value={form.about} onChange={set('about')} data-testid="apply-about" />
      <Button className="bg-indigo-600 hover:bg-indigo-700 text-white" disabled={busy} onClick={submit} data-testid="apply-submit">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Submit application'}
      </Button>
    </CardContent></Card>
  );
}

export default function PartnerPortalPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [helpSubject, setHelpSubject] = useState('');
  const [helpMsg, setHelpMsg] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { setData((await axios.get(`${API}/partners/me`, auth())).data); }
    catch { /* not logged in */ }
  }, []);
  useEffect(() => { if (user) load(); }, [user, load]);

  if (!user) return <PartnerLogin />;
  if (!data) return <div className="min-h-screen bg-background pt-28 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-muted-foreground" /></div>;

  const sendHelp = async () => {
    if (!helpSubject || !helpMsg) return toast.error('Add a subject and message');
    setBusy(true);
    try {
      await axios.post(`${API}/partners/help`, { subject: helpSubject, message: helpMsg }, auth());
      toast.success(`Sent to ${data.account_manager?.name || 'your account manager'} — we'll get back fast`);
      setHelpSubject(''); setHelpMsg('');
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not send'); }
    finally { setBusy(false); }
  };

  const approved = data.partner_status === 'approved';
  const links = [
    { icon: CalendarPlus, label: 'My Events & Create', desc: 'Publish and manage your events', to: '/admin/events' },
    { icon: BarChart3, label: 'Corporate Reports', desc: 'Sales, attendees, box office', to: '/reports' },
    { icon: Banknote, label: 'Payout History', desc: 'Every release and payment', to: '/payout-history' },
    { icon: Wallet, label: 'My Wallet', desc: 'Send money, withdraw to bank', to: '/money' },
  ];

  return (
    <div className="min-h-screen bg-background pt-24 pb-16">
      <div className="max-w-4xl mx-auto px-4 space-y-5" data-testid="partner-hub">
        <div>
          <h1 className="text-2xl font-extrabold flex items-center gap-2"><Building2 className="h-6 w-6 text-indigo-400" /> Partner Hub</h1>
          <p className="text-sm text-muted-foreground">Welcome back, {user.name}{data.org?.name ? ` — ${data.org.name}` : ''}.</p>
        </div>

        {approved ? (
          <Badge className="bg-emerald-500/15 text-emerald-400 border-emerald-500/30" data-testid="partner-status-badge"><CheckCircle2 className="h-3 w-3 mr-1" /> Approved partner{data.events_count ? ` · ${data.events_count} events` : ''}</Badge>
        ) : data.partner_status === 'pending' ? (
          <Card className="border-amber-500/40"><CardContent className="py-3">
            <Badge className="bg-amber-500/15 text-amber-400 border-amber-500/30" data-testid="partner-status-badge"><Clock className="h-3 w-3 mr-1" /> Application under review</Badge>
            <p className="text-xs text-muted-foreground mt-1.5">We review most applications within 1 business day — you'll get an email the moment you're approved.</p>
          </CardContent></Card>
        ) : (
          <ApplicationForm onDone={load} />
        )}

        {approved && (
          <div className="grid sm:grid-cols-2 gap-3">
            {links.map(l => (
              <button key={l.to} onClick={() => navigate(l.to)}
                className="text-left rounded-xl border border-border hover:border-indigo-500/60 transition-colors p-4 bg-card"
                data-testid={`partner-link-${l.to.replace(/\//g, '')}`}>
                <l.icon className="h-5 w-5 text-indigo-400 mb-2" />
                <p className="text-sm font-semibold">{l.label}</p>
                <p className="text-xs text-muted-foreground">{l.desc}</p>
              </button>
            ))}
          </div>
        )}

        <Card className="border-emerald-500/30"><CardContent className="py-4 space-y-1.5" data-testid="account-manager-card">
          <p className="text-sm font-semibold flex items-center gap-1.5"><UserRound className="h-4 w-4 text-emerald-400" /> Your account manager</p>
          <p className="text-sm">{data.account_manager?.name}</p>
          <p className="text-xs text-muted-foreground">{data.account_manager?.email}{data.account_manager?.phone ? ` · ${data.account_manager.phone}` : ''}</p>
        </CardContent></Card>

        {data.venues?.length > 0 && (
          <Card className="border-fuchsia-500/30"><CardContent className="py-4 space-y-1.5" data-testid="my-venues-card">
            <p className="text-sm font-semibold flex items-center gap-1.5"><Building2 className="h-4 w-4 text-fuchsia-400" /> Your venues</p>
            {data.venues.map(v => (
              <div key={`${v.city}-${v.venue_slug}`} className="flex items-center gap-3 flex-wrap text-sm">
                <button onClick={() => navigate(`/${v.city}/venues/${v.venue_slug}`)}
                  className="text-fuchsia-300 hover:underline" data-testid={`my-venue-${v.venue_slug}`}>
                  {v.venue_name} — view public page →
                </button>
                <button onClick={() => navigate(`/venue-manage/${v.city}/${v.venue_slug}`)}
                  className="text-xs px-2 py-0.5 rounded-full border border-fuchsia-500/40 text-fuchsia-300 hover:bg-fuchsia-500/10"
                  data-testid={`edit-venue-${v.venue_slug}`}>
                  Edit page
                </button>
              </div>
            ))}
          </CardContent></Card>
        )}

        <Card><CardContent className="py-4 space-y-2" data-testid="priority-help-card">
          <p className="text-sm font-semibold flex items-center gap-1.5"><LifeBuoy className="h-4 w-4 text-indigo-400" /> Priority help</p>
          <p className="text-xs text-muted-foreground">Stuck on anything — publishing, payouts, scanning? This goes straight to your account manager.</p>
          <Input placeholder="Subject" value={helpSubject} onChange={e => setHelpSubject(e.target.value)} data-testid="help-subject" />
          <Textarea placeholder="What do you need?" rows={3} value={helpMsg} onChange={e => setHelpMsg(e.target.value)} data-testid="help-message" />
          <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-white" disabled={busy} onClick={sendHelp} data-testid="help-send">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Send to my account manager'}
          </Button>
        </CardContent></Card>
      </div>
    </div>
  );
}
